import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { ArrowLeftIcon, PlusIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { ANNOUNCEMENT_TYPES } from '../../graphql/announcement-queries'
import {
  CREATE_ANNOUNCEMENT_TYPE,
  UPDATE_ANNOUNCEMENT_TYPE,
} from '../../graphql/announcement-mutations'
import LoadingSpinner from '../../components/LoadingSpinner'
import type { AnnouncementType } from '../../types/announcement'

interface FormState {
  key: string
  label: string
  requires_ack: boolean
  priority: number
  active: boolean
}

const EMPTY_FORM: FormState = { key: '', label: '', requires_ack: false, priority: 0, active: true }

const AnnouncementTypesPage = () => {
  const { data, loading, refetch } = useQuery(ANNOUNCEMENT_TYPES, {
    variables: { activeOnly: false },
    fetchPolicy: 'cache-and-network',
  })
  const types: AnnouncementType[] = data?.announcementTypes ?? []

  const [editing, setEditing] = useState<AnnouncementType | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  const [createType, { loading: creatingLoading }] = useMutation(CREATE_ANNOUNCEMENT_TYPE)
  const [updateType, { loading: updatingLoading }] = useMutation(UPDATE_ANNOUNCEMENT_TYPE)
  const saving = creatingLoading || updatingLoading

  const openCreate = () => {
    setForm(EMPTY_FORM)
    setEditing(null)
    setCreating(true)
  }

  const openEdit = (t: AnnouncementType) => {
    setForm({ key: t.key, label: t.label, requires_ack: t.requires_ack, priority: t.priority, active: t.active })
    setEditing(t)
    setCreating(false)
  }

  const close = () => { setCreating(false); setEditing(null) }

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error('Label is required'); return }
    try {
      if (editing) {
        await updateType({
          variables: {
            id: editing.id,
            input: {
              label: form.label.trim(),
              requires_ack: form.requires_ack,
              priority: form.priority,
              active: form.active,
            },
          },
        })
        toast.success('Type updated')
      } else {
        if (!form.key.trim()) { toast.error('Key is required'); return }
        await createType({
          variables: {
            input: {
              key: form.key.trim(),
              label: form.label.trim(),
              requires_ack: form.requires_ack,
              priority: form.priority,
              active: form.active,
            },
          },
        })
        toast.success('Type created')
      }
      close()
      void refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save type')
    }
  }

  const isOpen = creating || !!editing

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/admin/announcements" className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Announcement Types</h1>
            <p className="mt-1 text-sm text-gray-500">Types carry the acknowledgement requirement and feed priority.</p>
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2">
          <PlusIcon className="h-4 w-4" />
          New Type
        </button>
      </div>

      {/* List */}
      {loading && types.length === 0 ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {types.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900">{t.label}</span>
                  <span className="text-xs text-gray-400">{t.key}</span>
                  {t.requires_ack && (
                    <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-medium">requires ack</span>
                  )}
                  {!t.active && (
                    <span className="rounded-full bg-gray-100 text-gray-500 px-2 py-0.5 text-xs font-medium">inactive</span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-gray-400">priority {t.priority}</p>
              </div>
              <button
                onClick={() => openEdit(t)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 flex-shrink-0"
              >
                <PencilIcon className="h-4 w-4" />
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={close} />
            <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">{editing ? 'Edit Type' : 'New Type'}</h3>
                <button onClick={close} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
              </div>
              <div className="px-6 py-5 space-y-4">
                {/* Key — immutable after creation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.key}
                    onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                    placeholder="e.g. procedure_change"
                    className="input w-full disabled:bg-secondary-50"
                    disabled={!!editing}
                  />
                  {editing && <p className="mt-1 text-xs text-gray-400">Key can't be changed after creation.</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.label}
                    onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                    placeholder="e.g. Procedure change"
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))}
                    className="input w-full"
                  />
                  <p className="mt-1 text-xs text-gray-400">Higher shows first in the feed.</p>
                </div>

                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.requires_ack}
                    onChange={(e) => setForm((f) => ({ ...f, requires_ack: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  Requires acknowledgement
                </label>

                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  Active (selectable when authoring)
                </label>
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
                <button onClick={close} className="btn-secondary">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="btn-primary">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AnnouncementTypesPage
